import React, { FC } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useInactivityTracker, formatTimeSinceActivity, InactivityConfig } from "../../utils/inactivityTracker";
import "./inactivityIndicator.css";

interface InactivityIndicatorProps extends InactivityConfig {
	showTimeSinceActivity?: boolean;
	showChip?: boolean;
	className?: string;
	onInactive?: () => void;
	onActive?: () => void;
}

const InactivityIndicator: FC<InactivityIndicatorProps> = ({
	timeout = 300000, // 5 minutes default
	showTimeSinceActivity = true,
	showChip = true,
	className = "",
	onInactive,
	onActive,
	...config
}) => {
	const { isInactive, timeSinceLastActivity } = useInactivityTracker({
		timeout,
		onInactive,
		onActive,
		...config
	});

	const handleInactive = () => {
		onInactive?.();
	};

	const handleActive = () => {
		onActive?.();
	};

	// Use the handlers from the hook
	React.useEffect(() => {
		if (isInactive) {
			handleInactive();
		} else {
			handleActive();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInactive]);

	if (!showChip && !showTimeSinceActivity) {
		return null;
	}

	return (
		<Box className={`inactivity-indicator ${className}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			{showChip && (
				<Tooltip title={isInactive ? "User is inactive" : "User is active"}>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							padding: "4px 8px",
							borderRadius: "12px",
							border: "1px solid",
							borderColor: isInactive ? "warning.main" : "success.main",
							backgroundColor: isInactive ? "warning.light" : "success.light",
							color: isInactive ? "warning.dark" : "success.dark",
							fontSize: "0.75rem",
							fontWeight: 500,
							textTransform: "uppercase",
							letterSpacing: "0.5px",
							minWidth: "60px",
							justifyContent: "center",
							"&::before": {
								content: '""',
								display: "inline-block",
								width: "6px",
								height: "6px",
								borderRadius: "50%",
								backgroundColor: isInactive ? "warning.main" : "success.main",
								marginRight: "6px",
								animation: isInactive ? "pulse 2s infinite" : "none"
							}
						}}
					>
						{isInactive ? "Inactive" : "Active"}
					</Box>
				</Tooltip>
			)}

			{showTimeSinceActivity && (
				<Tooltip title="Time since last activity">
					<Typography variant="caption" color="text.secondary">
						{formatTimeSinceActivity(timeSinceLastActivity)}
					</Typography>
				</Tooltip>
			)}
		</Box>
	);
};

export default InactivityIndicator;
